/**
 * Tag normalization — the single source of truth for how a free-form tag
 * name is canonicalized before it's compared, deduped, or matched.
 *
 * Tags are household-wide, case-insensitive labels. The display value keeps
 * the user's casing (it's what's stored and shown on the chip); equality is
 * decided on the normalized form so "Home", "home", and " home " are one tag.
 */

/** Trim surrounding whitespace; preserves the user's casing for display. */
export function normalizeTagInput(raw: string): string {
  return raw.trim();
}

/** Casefold + trim for equality / dedup / membership comparisons. */
export function tagKey(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Add `tag` to `existing`, trimming it and rejecting case-insensitive
 * duplicates and empties. Returns the input array unchanged (same reference)
 * when the tag is empty or already present, so callers can skip a no-op
 * onChange.
 */
export function addTag(existing: string[], tag: string): string[] {
  const trimmed = normalizeTagInput(tag);
  if (!trimmed) return existing;
  const key = tagKey(trimmed);
  if (existing.some((t) => tagKey(t) === key)) return existing;
  return [...existing, trimmed];
}

/** Remove the tag matching `tag` case-insensitively. */
export function removeTag(existing: string[], tag: string): string[] {
  const key = tagKey(tag);
  return existing.filter((t) => tagKey(t) !== key);
}
